const HANGUL = /\p{Script=Hangul}/u;
const GOOGLE_TRANSLATE_ENDPOINT = 'https://translate.googleapis.com/translate_a/single';
export const TRANSLATE_UNIT_DELIM = '§UGOT§';

export function joinTranslateUnits(texts) {
  return texts.map((text) => String(text || '')).join(`\n${TRANSLATE_UNIT_DELIM}\n`);
}

export function splitTranslateUnits(blob, count) {
  const size = Number(count) || 0;
  if (size <= 0) return [];
  const parts = String(blob || '').split(/\s*§UGOT§\s*/).map((part) => part.trim());
  const out = [];
  for (let i = 0; i < size; i += 1) out.push(parts[i] || '');
  return out;
}

export function inferSourceLang(text) {
  const src = String(text || '');
  if (HANGUL.test(src)) return 'ko';
  if (/[A-Za-z]/.test(src)) return 'en';
  return 'auto';
}

export function inferTargetLang(source) {
  if (source === 'ko') return 'en';
  if (source === 'en') return 'ko';
  return 'en';
}

export function parseGoogleTranslateResponse(data) {
  if (Array.isArray(data) && typeof data[0] === 'string') return data[0];
  if (!Array.isArray(data) || !Array.isArray(data[0])) return '';
  return data[0]
    .map((part) => (Array.isArray(part) ? String(part[0] || '') : ''))
    .join('');
}

function googleTranslateUrl(text, source, target, endpoint) {
  const url = new URL(endpoint);
  url.searchParams.set('client', 'gtx');
  url.searchParams.set('sl', source);
  url.searchParams.set('tl', target);
  url.searchParams.set('dt', 't');
  url.searchParams.set('q', text);
  return url;
}

async function translateWithNativeTranslator(text, source, target) {
  if (source === 'auto' || typeof globalThis.Translator?.create !== 'function') return null;
  const translator = await Promise.race([
    Translator.create({
      sourceLanguage: source,
      targetLanguage: target,
    }),
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error('google-translate:native-timeout')), 1500);
    }),
  ]);
  return String(await translator.translate(text) || '').trim();
}

export async function translateWithGoogle(text, options = {}) {
  const q = String(text || '').trim();
  if (!q) return '';
  const source = options.source || inferSourceLang(q);
  const target = options.target || inferTargetLang(source);
  if (source === target) return q;

  const allowNative = options.allowNativeTranslator !== false;
  if (allowNative) {
    try {
      const native = await translateWithNativeTranslator(q, source, target);
      if (native) return native;
    } catch {
      // Google Translate HTTP is the fallback.
    }
  }

  const fetchImpl = options.fetchImpl || globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    throw new Error('google-translate:fetch-missing');
  }

  if (options.proxyUrl) {
    const origin = options.baseUrl || (typeof globalThis.location?.origin === 'string'
      ? globalThis.location.origin
      : 'http://127.0.0.1/');
    const proxy = new URL(options.proxyUrl, origin);
    proxy.searchParams.set('q', q);
    proxy.searchParams.set('sl', source);
    proxy.searchParams.set('tl', target);
    const proxied = await fetchImpl(proxy.href);
    if (!proxied.ok) throw new Error(`google-translate:${proxied.status}`);
    return parseGoogleTranslateResponse(await proxied.json());
  }

  const res = await fetchImpl(googleTranslateUrl(
    q,
    source,
    target,
    options.endpoint || GOOGLE_TRANSLATE_ENDPOINT,
  ).href);
  if (!res.ok) throw new Error(`google-translate:${res.status}`);
  return parseGoogleTranslateResponse(await res.json());
}
